import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { BRAND_NAME } from './brand';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import AppOverlays from './components/AppOverlays';
import { CustomerPresentationProvider } from './contexts/CustomerPresentationContext';
// New-deploy detection: polls /version.json and shows a persistent, opt-in
// "Refresh" toast. Sits OUTSIDE the ErrorBoundary so it survives chunk-error
// crashes (whose fallback it complements with a required-refresh variant).


// Route-level code splitting (perf review P22): every page below is lazy so a
// user downloads only the screen they visit, not all ~35 pages up front. Login
// stays eager — it's the logged-out first paint. Shared chrome (AuthProvider,
// Layout, ProtectedRoute) stays eager because every page needs it anyway.
import Login from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const FeedReview = lazy(() => import('./pages/FeedReview'));
const MarketOpportunities = lazy(() => import('./pages/MarketOpportunities'));
const Arbitrations = lazy(() => import('./pages/Arbitrations'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
const PartsInbox = lazy(() => import('./pages/PartsInbox'));
const PartsCatalog = lazy(() => import('./pages/PartsCatalog'));
const EstimateApprovals = lazy(() => import('./pages/EstimateApprovals'));
const VendorTasks = lazy(() => import('./pages/VendorTasks'));
const Recon = lazy(() => import('./pages/recon/Recon'));
const ReconTasks = lazy(() => import('./pages/recon/ReconTasks'));
const Settings = lazy(() => import('./pages/Settings'));
const DealershipHistory = lazy(() => import('./pages/DealershipHistory'));
const Invoices = lazy(() => import('./pages/Invoices'));
const CRM = lazy(() => import('./pages/CRM'));
const Evaluator = lazy(() => import('./pages/Evaluator'));
const EvaluatorSettings = lazy(() => import('./pages/EvaluatorSettings'));
const WebsiteSettings = lazy(() => import('./pages/WebsiteSettings'));
const WebsiteContent = lazy(() => import('./pages/WebsiteContent'));
const BlogPosts = lazy(() => import('./pages/BlogPosts'));
const CRMLeadDetail = lazy(() => import('./pages/CRMLeadDetail'));
// Customer Intelligence Phase 1 (docs/specs/CUSTOMER_INTELLIGENCE_PLATFORM.md):
// the person-centric Shopper 360. /crm/data (health + duplicate review) is a tab
// on the CRM hub, so it routes through <CRM /> below like the other tabs.
const Shopper360 = lazy(() => import('./pages/Shopper360'));
const Reports = lazy(() => import('./pages/Reports'));
const Marketing = lazy(() => import('./pages/Marketing'));
// Accounting (cost rollups) + the F&I menu builder moved from top-nav routes into
// Settings sections (header declutter) — see components/AccountingSection.js +
// components/FIMenuSection.js. Per-deal F&I (DealDesk) + public /fi/:token stay.
// DMS module (gated). Pages render content only, so they're wrapped in <Layout>
// at the route (mirrors how the other module pages sit in the app shell).
import { Layout } from './components/Layout';
const QboReconciliationQueue = lazy(() => import('./components/QboReconciliationQueue'));
const DealList = lazy(() => import('./dms/DealList'));
const DealDesk = lazy(() => import('./dms/DealDesk'));
const FinanceDesk = lazy(() => import('./dms/finance/FinanceDesk'));
const FormTemplates = lazy(() => import('./dms/FormTemplates'));
const SoldCustomers = lazy(() => import('./dms/SoldCustomers'));
const Vendors = lazy(() => import('./pages/Vendors'));
const VendorDetail = lazy(() => import('./pages/VendorDetail'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Admin = lazy(() => import('./pages/Admin'));
const ParserHealth = lazy(() => import('./pages/ParserHealth'));
// Billing & Subscriptions module (Slice 1). SuperAdmin platform billing page;
// dealer self-service Billing is a section inside Settings (BillingSection).
const AdminBilling = lazy(() => import('./pages/AdminBilling'));
// SHARED-SURFACE EDIT (append-only) — SuperAdmin AI model lifecycle page.
const AIModels = lazy(() => import('./pages/AIModels'));
const InternalLanding = lazy(() => import('./pages/InternalLanding'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const PublicBooking = lazy(() => import('./pages/PublicBooking'));
// PUBLIC (no auth) tokenized F&I-menu customer view — route /fi/:token below.
const PublicFIMenu = lazy(() => import('./pages/PublicFIMenu'));
// SHARED-SURFACE EDIT (append-only) — Platform Admin: SuperAdmin-only dealer onboarding/provisioning.
const Onboarding = lazy(() => import('./pages/Onboarding'));
// SHARED-SURFACE EDIT (append-only) — Platform Admin: SuperAdmin dealership lifecycle (suspend/offboard).
const DealershipLifecycle = lazy(() => import('./pages/DealershipLifecycle'));

// Matches the ProtectedRoute auth-check spinner so a chunk load and an auth
// check are visually indistinguishable.
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  // Brand the document title from the single source of truth (brand.js) so the
  // static index.html <title> doesn't pin the name. See docs/specs/BRANDING.md.
  useEffect(() => { document.title = BRAND_NAME; }, []);
  return (
    <AuthProvider>
      <CustomerPresentationProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          {/* PUBLIC customer self-booking (tenant resolved server-side from the token) */}
          <Route path="/book/:token" element={<PublicBooking />} />
          {/* PUBLIC customer F&I menu (DMS) — tenant + frozen menu resolved from the token */}
          <Route path="/fi/:token" element={<PublicFIMenu />} />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />

          {/* SHARED-SURFACE EDIT (append-only) — Platform Admin: SuperAdmin-only
              dealership lifecycle (suspend/reactivate/offboard). Page enforces
              SuperAdmin internally (no requireDealership). */}
          <Route
            path="/dealership-lifecycle"
            element={
              <ProtectedRoute>
                <DealershipLifecycle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/parser-health"
            element={
              <ProtectedRoute>
                <ParserHealth />
              </ProtectedRoute>
            }
          />

          {/* SHARED-SURFACE EDIT (append-only) — Platform Admin: SuperAdmin-only
              dealer onboarding/provisioning. Page enforces SuperAdmin internally
              (no requireDealership — SuperAdmin has no dealership_id). */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          {/* SuperAdmin platform billing (Slice 1). Server enforces require_super_admin
              on every /billing/admin API. Dealer self-service Billing now lives as an
              Owner-only section inside Settings — no standalone /billing route. */}
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute>
                <AdminBilling />
              </ProtectedRoute>
            }
          />

          {/* SHARED-SURFACE EDIT (append-only) — SuperAdmin AI model lifecycle
              (docs/specs/AI_MODEL_LIFECYCLE.md). Server enforces require_super_admin
              on every /admin/ai-models API; the page also gates on role. */}
          <Route
            path="/admin/ai-models"
            element={
              <ProtectedRoute>
                <AIModels />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireDealership>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vehicles"
            element={
              <ProtectedRoute requireDealership>
                <Vehicles />
              </ProtectedRoute>
            }
          />
          
          {/* Declared before /vehicles/:id so the literal path wins the match. */}
          <Route
            path="/vehicles/feed-review"
            element={
              <ProtectedRoute requireDealership>
                <FeedReview />
              </ProtectedRoute>
            }
          />

          {/* Inventory pilot: literal route before /vehicles/:id; API enforces access. */}
          <Route
            path="/vehicles/market-opportunities"
            element={
              <ProtectedRoute requireDealership>
                <MarketOpportunities />
              </ProtectedRoute>
            }
          />

          {/* Auction arbitration queue (Owner/Manager enforced by the page + API). */}
          <Route
            path="/arbitrations"
            element={
              <ProtectedRoute requireDealership>
                <Arbitrations />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicles/:id"
            element={
              <ProtectedRoute requireDealership>
                <VehicleDetail />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/parts-inbox"
            element={
              <ProtectedRoute requireDealership>
                <PartsInbox />
              </ProtectedRoute>
            }
          />

          {/* Parts-catalog operator page (entitlement-gated module). The API
              gate (require_module_entitlement("parts")) is the real
              enforcement — a non-entitled dealer that forces /parts gets 403. */}
          <Route
            path="/parts"
            element={
              <ProtectedRoute requireDealership>
                <PartsCatalog />
              </ProtectedRoute>
            }
          />

          <Route
            path="/estimates"
            element={
              <ProtectedRoute requireDealership>
                <EstimateApprovals />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vendors"
            element={
              <ProtectedRoute requireDealership>
                <Vendors />
              </ProtectedRoute>
            }
          />

          {/* Per-vendor detail: unified message feed (SMS/email) + that
              vendor's invoices. */}
          <Route
            path="/vendors/:id"
            element={
              <ProtectedRoute requireDealership>
                <VendorDetail />
              </ProtectedRoute>
            }
          />

          {/* Recon hub (formerly "Tasks") — bucket cards landing page. */}
          <Route
            path="/recon"
            element={
              <ProtectedRoute requireDealership>
                <Recon />
              </ProtectedRoute>
            }
          />

          {/* Prefiltered recon task list, driven by ?bucket=<key>. */}
          <Route
            path="/recon/tasks"
            element={
              <ProtectedRoute requireDealership>
                <ReconTasks />
              </ProtectedRoute>
            }
          />

          {/* Back-compat: old /tasks deep links land on the Recon hub. */}
          <Route path="/tasks" element={<Navigate to="/recon" replace />} />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute requireDealership>
                <Invoices />
              </ProtectedRoute>
            }
          />

          <Route
            path="/evaluator"
            element={
              <ProtectedRoute requireDealership>
                <Evaluator />
              </ProtectedRoute>
            }
          />

          <Route
            path="/marketing"
            element={
              <ProtectedRoute requireDealership>
                <Marketing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/evaluator/settings"
            element={
              <ProtectedRoute requireDealership>
                <EvaluatorSettings />
              </ProtectedRoute>
            }
          />

          {/* Website design + Blog live under Marketing (operator decision
              2026-07-08); the old /settings/* paths redirect so bookmarks and
              muscle memory keep working. */}
          <Route
            path="/marketing/website"
            element={
              <ProtectedRoute requireDealership>
                <WebsiteSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/settings/website" element={<Navigate to="/marketing/website" replace />} />
          <Route
            path="/marketing/website/content"
            element={
              <ProtectedRoute requireDealership>
                <WebsiteContent />
              </ProtectedRoute>
            }
          />

          <Route
            path="/marketing/blog"
            element={
              <ProtectedRoute requireDealership>
                <BlogPosts />
              </ProtectedRoute>
            }
          />
          <Route path="/settings/blog" element={<Navigate to="/marketing/blog" replace />} />

          <Route
            path="/crm"
            element={
              <ProtectedRoute requireDealership>
                <CRM />
              </ProtectedRoute>
            }
          />

          {/* CRM hub tabs — same hub component, tab chosen from the path
              (docs/specs/CRM_WORK_PLAN.md). Calendar was its own top-level nav
              item; it now lives here, and /appointments redirects in below. */}
          <Route
            path="/crm/my-day"
            element={
              <ProtectedRoute requireDealership>
                <CRM />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/calendar"
            element={
              <ProtectedRoute requireDealership>
                <CRM />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/review"
            element={
              <ProtectedRoute requireDealership>
                <CRM />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/data"
            element={
              <ProtectedRoute requireDealership>
                <CRM />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/shoppers/:contactId"
            element={
              <ProtectedRoute requireDealership>
                <Shopper360 />
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm/leads/:leadId"
            element={
              <ProtectedRoute requireDealership>
                <CRMLeadDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute requireDealership>
                <Reports />
              </ProtectedRoute>
            }
          />

          <Route path="/dms/accounting" element={<ProtectedRoute requireDealership><Layout><QboReconciliationQueue /></Layout></ProtectedRoute>} />
          {/* DMS module (gated). requireDealership guards the route client-side;
              the API gate (require_module_entitlement("dms")) is the real
              enforcement — a non-entitled dealer that forces /dms gets 403. */}
          <Route
            path="/dms/deals"
            element={
              <ProtectedRoute requireDealership>
                <Layout><DealList /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dms/customers"
            element={
              <ProtectedRoute requireDealership>
                <Layout><SoldCustomers /></Layout>
              </ProtectedRoute>
            }
          />
          {/* /dms/fi/menus (the F&I menu builder) moved into Settings — see
              components/FIMenuSection.js. The per-deal F&I panel lives on /dms/deals/:id. */}
          <Route
            path="/dms/forms"
            element={
              <ProtectedRoute requireDealership>
                <Layout><FormTemplates /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dms/deals/:id"
            element={
              <ProtectedRoute requireDealership>
                <Layout><DealDesk /></Layout>
              </ProtectedRoute>
            }
          />
          {/* Finance desk (Deal Router / Matcher / Max Price) — standalone and
              deal-launched (prefilled via the finance-context allowlist). */}
          <Route
            path="/dms/finance"
            element={
              <ProtectedRoute requireDealership>
                <Layout><FinanceDesk /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dms/deals/:id/finance"
            element={
              <ProtectedRoute requireDealership>
                <Layout><FinanceDesk /></Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dealership-history"
            element={
              <ProtectedRoute requireDealership>
                <DealershipHistory />
              </ProtectedRoute>
            }
          />
          
          {/* Calendar moved into the CRM hub. Keep /appointments working for
              booking confirmations, lead-detail links, and old bookmarks. */}
          <Route path="/appointments" element={<Navigate to="/crm/calendar" replace />} />

          <Route
            path="/vendor/tasks"
            element={
              <ProtectedRoute requireVendor>
                <VendorTasks />
              </ProtectedRoute>
            }
          />
          
          <Route path="/" element={<InternalLanding />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      <AppOverlays />
      </CustomerPresentationProvider>
    </AuthProvider>
  );
}

export default App;
